/**
 * schemas/sync
 * The outbox — what this subledger has told Payroll and Billing, and whether
 * it landed.
 *
 * `landed` is invariant 2 at the glass: a row with no reference from the
 * destination has NOT arrived, whatever its status column says. Retry re-sends
 * the same event identity, so it is idempotent at the far end.
 */
import { z } from 'zod';
import { isoDate, isoDateNullable } from '../common';
import { gateStateSchema } from '../config/gates';

export const SYNC_STATUSES = ['PENDING', 'DELIVERED', 'FAILED'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

export const syncRowSchema = z.object({
  id: z.string(),
  /** `cash.assignment.opened`, `cash.spend.routed`, … — rendered in mono. */
  event: z.string(),
  target: z.string().nullable(),
  aggregate_id: z.string(),
  /** The destination's reference. Null means it has not landed. */
  ref: z.string().nullable(),
  status: z.string(),
  attempts: z.number(),
  note: z.string().nullable(),
  last_error: z.string().nullable(),
  created_at: isoDate,
  delivered_at: isoDateNullable,
  landed: z.boolean(),
});
export type SyncRow = z.infer<typeof syncRowSchema>;

/**
 * GET /v1/sync — verified against the live API.
 *
 * The rows are under `rows`, NOT `events`, and the response carries the gate
 * map and the dead-letter count alongside them. The gate arrives in the BODY
 * with a 200 rather than as a 412: an unconfigured workspace can still see its
 * (empty) log, and it is the bar that tells the operator why.
 */
export const syncResponseSchema = z.object({
  gates: gateStateSchema,
  /** Rows that exhausted their retries. Worth surfacing — they are stuck. */
  dead_letters: z.number(),
  rows: z.array(syncRowSchema),
});
export type SyncResponse = z.infer<typeof syncResponseSchema>;

/** Retry is offered on failures only — a delivered row has nothing to resend. */
export const canRetry = (row: SyncRow): boolean => row.status === 'FAILED';
